import React from "react";
import { Link } from "react-router-dom";
import { X, Plus, Minus, Trash2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { resolveImg, fmt } from "@/lib/api";

export default function CartDrawer() {
  const { items, open, setOpen, setQty, remove, subtotal, itemKey } = useCart();
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50" data-testid="cart-drawer">
      <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <aside className="absolute right-0 top-0 h-full w-full sm:w-[420px] bg-slate-950 border-l border-white/10 flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div>
            <div className="label-mono text-cyan-400">Your Cart</div>
            <div className="text-lg font-medium mt-0.5">{items.length} item{items.length !== 1 && "s"}</div>
          </div>
          <button onClick={() => setOpen(false)} className="p-2 text-slate-400 hover:text-white" data-testid="cart-close"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-auto px-5 py-4 space-y-4">
          {items.length === 0 && (
            <div className="text-center text-slate-500 py-20 text-sm">
              Your cart is empty.
            </div>
          )}
          {items.map((i) => {
            const k = itemKey(i);
            return (
            <div key={k} className="flex gap-3 border border-white/5 p-3" data-testid={`cart-item-${i.product_id}`}>
              <img src={resolveImg(i.image_url)} alt={i.name} className="w-16 h-16 object-cover" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{i.name}</div>
                {i.variant_label && (
                  <div className="label-mono text-cyan-400/80 mt-0.5" data-testid={`cart-item-variant-${i.product_id}`}>{i.variant_label}</div>
                )}
                <div className="font-mono text-xs text-cyan-400 mt-0.5">{fmt(i.price)}</div>
                <div className="flex items-center gap-2 mt-2">
                  <button onClick={() => setQty(k, i.quantity - 1)} className="w-6 h-6 border border-white/10 flex items-center justify-center hover:border-cyan-500/50" data-testid={`qty-dec-${i.product_id}`}><Minus size={12} /></button>
                  <span className="font-mono text-sm w-6 text-center" data-testid={`qty-val-${i.product_id}`}>{i.quantity}</span>
                  <button onClick={() => setQty(k, i.quantity + 1)} className="w-6 h-6 border border-white/10 flex items-center justify-center hover:border-cyan-500/50" data-testid={`qty-inc-${i.product_id}`}><Plus size={12} /></button>
                  <button onClick={() => remove(k)} className="ml-auto text-slate-500 hover:text-red-400" data-testid={`cart-remove-${i.product_id}`}><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
            );
          })}
        </div>

        <div className="p-5 border-t border-white/10 space-y-4">
          <div className="flex justify-between text-sm">
            <span className="text-slate-400">Subtotal</span>
            <span className="font-mono" data-testid="cart-subtotal">{fmt(subtotal)}</span>
          </div>
          <Link
            to="/checkout"
            onClick={() => setOpen(false)}
            data-testid="cart-checkout-btn"
            className={`block text-center py-3 font-semibold tracking-wide transition-colors ${items.length === 0 ? "bg-slate-800 text-slate-500 pointer-events-none" : "bg-cyan-500 hover:bg-cyan-400 text-slate-950"}`}
          >
            Proceed to Checkout
          </Link>
          <Link to="/shop" onClick={() => setOpen(false)} className="block text-center text-xs text-slate-400 hover:text-cyan-400">
            Continue shopping →
          </Link>
        </div>
      </aside>
    </div>
  );
}
